import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { Cookie } from '@vue-util/cookie';
import { isStoreOpen, parseStoreHours } from '../utils/storeHoursUtil';
import getCSLCookie from '~/composables/getCSLCookie';
import { getCommonStore, getLocationInfoByZipCode, getLocationsInfo, getZipCode, getStoreInventoryInfo } from '../utils/deliveryAndPickupUtil';
import type { LocationInfo, StoreInfo, StoreInventory, RelativeDay } from '../types/delivery-pickup-info';
import { GetItCopy, GetItType } from '../types/delivery-pickup-info';
import { TODAY, TOMORROW, REQUIRED_STORE_FIELDS } from '../constants/delivery-and-pickup';
import type { StoreDetails } from '../types/common-store';
import parseOrderByTime from '../utils/parseOrderByTime';

export const useDeliveryAndPickupStore = defineStore('deliveryAndPickupStore', () => {
  const sddZipCode = ref<string | null>(null);
  const sddZipCodeLoading = ref<boolean>(false);
  const sflLoaded = ref<boolean>(false);
  const bopsStore = ref<StoreDetails | null>(null);
  const storeLocation = ref<number | null | undefined>(); // CSL
  const cslLoaded = ref<boolean>(false);
  const shipFromLocation = ref<number | null>(null); // SFL
  const eligibility = ref<Record<number, StoreInventory | null>>({});
  const locations = ref<Record<number, StoreInfo | null>>({});
  const locationsLoading = ref<boolean>(false);
  const inventoryLoading = ref<boolean>(false);
  let observers: (() => void)[] = [];

  const isClient = import.meta.client;

  const sddLocationInfo = computed(() => {
    return shipFromLocation.value ? locations.value[shipFromLocation.value] : null;
  });

  const pickupLocationInfo = computed(() => {
    return storeLocation.value ? locations.value[storeLocation.value] : null;
  });

  const bopsWorkingHours = computed(() => {
    if (!isClient || !pickupLocationInfo.value?.schedule) return null;
    return parseStoreHours(pickupLocationInfo.value.schedule);
  });

  const sddWorkingHours = computed(() => {
    if (!isClient || !sddLocationInfo.value?.schedule) return null;
    return parseStoreHours(sddLocationInfo.value.schedule);
  });

  const isStoreClosed = computed(() => {
    if (!isClient) return { today: false, tomorrow: false };
    const schedule = pickupLocationInfo.value?.schedule;
    if (!schedule) {
      return {
        today: false,
        tomorrow: false,
      };
    }

    const workingHoursDisplay = parseStoreHours(schedule);
    const todayHours = workingHoursDisplay?.today;
    const tomorrowHours = workingHoursDisplay?.tomorrow;

    return {
      today: !todayHours || !isStoreOpen(todayHours),
      tomorrow: !tomorrowHours || !isStoreOpen(tomorrowHours),
    };
  });

  const isSddClosed = computed(() => {
    if (!isClient) return { today: false, tomorrow: false };
    const schedule = sddLocationInfo.value?.schedule;
    if (!schedule) {
      return {
        today: false,
        tomorrow: false,
      };
    }

    const workingHoursDisplay = parseStoreHours(schedule);
    const todayHours = workingHoursDisplay?.today;
    const tomorrowHours = workingHoursDisplay?.tomorrow;
    return {
      today: !todayHours || !isStoreOpen(todayHours),
      tomorrow: !tomorrowHours || !isStoreOpen(tomorrowHours),
    };
  });

  const isStoreClosedToday = computed(() => isStoreClosed.value.today);
  const isSddClosedToday = computed(() => isSddClosed.value.today);

  const pickupRelativeDay = computed(() => {
    const cutoffDay = pickupLocationInfo.value?.cutOffTime?.pickupRelativeDay?.toUpperCase();
    if (!cutoffDay || !['TODAY', 'TOMORROW'].includes(cutoffDay)) return undefined;

    // Rollover: If scheduled for TODAY but the store is closed today,
    // and the store is open tomorrow, roll over to 'tomorrow'.
    if (cutoffDay === TODAY && isStoreClosed.value.today) {
      if (!isStoreClosed.value.tomorrow) {
        return 'tomorrow';
      }
    }

    return cutoffDay.toLowerCase();
  });

  const deliveryRelativeDay = computed(() => {
    const cutoffDay = sddLocationInfo.value?.sddCutOffTime?.deliveryRelativeDay?.toUpperCase();
    if (!cutoffDay || !['TODAY', 'TOMORROW'].includes(cutoffDay)) return undefined;

    // Rollover: If scheduled for TODAY but delivery fulfillment store is closed today,
    // and open tomorrow, roll over to 'tomorrow'.
    if (cutoffDay === TODAY && isSddClosed.value.today) {
      if (!isSddClosed.value.tomorrow) {
        return 'tomorrow';
      }
    }

    return cutoffDay.toLowerCase();
  });

  const checkEligibilityByWorkingHours = (
    cutoffDay: RelativeDay | undefined,
    workingHours: { today: string | null; tomorrow: string | null } | null,
  ) => {
    if (!cutoffDay || !['TODAY', 'TOMORROW'].includes(cutoffDay)) return false;

    if (!workingHours) {
      return true;
    }

    const isOpenToday = isStoreOpen(workingHours.today);
    const isOpenTomorrow = isStoreOpen(workingHours.tomorrow);

    // When cutoffDay is TODAY, allow eligibility if open today OR open tomorrow (reopening rollover case, e.g. 12/25)
    if (cutoffDay === TODAY) {
      return isOpenToday || isOpenTomorrow;
    }

    // When cutoffDay is TOMORROW (e.g. post-cutoff on 12/23), only eligible if open tomorrow
    if (cutoffDay === TOMORROW) {
      return isOpenTomorrow;
    }

    return false;
  };

  const bopsEligible = computed(() => {
    if (!storeLocation.value || !eligibility.value[storeLocation.value]?.bopsEligible) return false;

    const cutOff = pickupLocationInfo.value?.cutOffTime;
    const cutoffDay = cutOff?.pickupRelativeDay?.toUpperCase() as RelativeDay;

    return checkEligibilityByWorkingHours(cutoffDay, bopsWorkingHours.value);
  });

  const sddEligible = computed(() => {
    if (!shipFromLocation.value || !sddLocationInfo.value) return false;

    const cutOff = sddLocationInfo.value?.sddCutOffTime;
    const cutoffDay = cutOff?.deliveryRelativeDay?.toUpperCase();
    return checkEligibilityByWorkingHours(cutoffDay as RelativeDay, sddWorkingHours.value);
  });

  const getItDeliveryType = computed(() => {
    const rawPickupDay = pickupRelativeDay.value?.toUpperCase();
    const rawDeliveryDay = deliveryRelativeDay.value?.toUpperCase();

    const pickupEligible = Boolean(rawPickupDay && bopsEligible.value);
    const deliveryEligible = Boolean(rawDeliveryDay && sddEligible.value);

    const isPickupToday = rawPickupDay === TODAY;
    const isDeliveryToday = rawDeliveryDay === TODAY;

    if (pickupEligible && deliveryEligible) {
      if (isPickupToday && isDeliveryToday) return GetItType.getItToday;
      if (isPickupToday || isDeliveryToday) return GetItType.getItFast;
      return GetItType.getItTomorrow;
    }

    if (pickupEligible || deliveryEligible) {
      const isRelativeToday = pickupEligible ? isPickupToday : isDeliveryToday;
      if (isRelativeToday) return GetItType.getItToday;
      return GetItType.getItTomorrow;
    }

    if (isStoreClosed.value.today || isSddClosed.value.today) {
      return GetItType.getItFast;
    }

    return GetItType.getItToday;
  });

  const getItLabel = computed(() => {
    return GetItCopy[getItDeliveryType.value];
  });

  const pickUpOrderByMsg = computed<string | null>(() => {
    const { cutOffTime } = pickupLocationInfo.value || {};

    if (cutOffTime && pickupRelativeDay.value === 'today' && bopsEligible.value) {
      const { hour } = cutOffTime.orderTimeThreshold || {};
      return parseOrderByTime(hour);
    }

    return null;
  });

  const sddOrderByMsg = computed<string | null>(() => {
    const { sddCutOffTime } = sddLocationInfo.value || {};

    if (sddCutOffTime && deliveryRelativeDay.value === 'today' && sddEligible.value) {
      const { hour } = sddCutOffTime.orderTimeThreshold || {};
      return parseOrderByTime(hour);
    }

    return null;
  });

  const storeZipCode = computed(() => {
    return bopsStore.value?.address?.zipCode;
  });

  const isBopsEligibleForBadge = computed(() => {
    return !!(storeLocation.value && bopsEligible.value && pickupRelativeDay.value);
  });

  const isSddEligibleForBadge = computed(() => {
    return !!(shipFromLocation.value && sddEligible.value && sddZipCode.value && deliveryRelativeDay.value);
  });

  const setSddZipCode = (zip: string | null) => {
    sddZipCode.value = zip;
  };

  const setBopsStore = (store: StoreDetails) => {
    bopsStore.value = store;
  };

  const setStoreLocation = (locationNumber: number | null = null) => {
    storeLocation.value = locationNumber;
  };

  const setLocationEligibility = (locationNumber: number, storeInventory: StoreInventory | null) => {
    eligibility.value = {
      ...eligibility.value,
      [locationNumber]: storeInventory,
    };
  };

  const setLocationInfo = (locationNumber: number, data: StoreInfo | null) => {
    locations.value = {
      ...locations.value,
      [locationNumber]: data,
    };
  };

  const notifySubscribers = () => {
    if (observers.length > 0) {
      observers.forEach((fn) => fn());
      observers = [];
    }
  };

  watch([storeLocation, shipFromLocation], async ([newStore, newShip], [oldStore, oldShip]) => {
    const locationIds = [newStore, newShip].filter((id): id is number => !!id);
    if (locationIds.length > 0 && (newStore !== oldStore || newShip !== oldShip)) {
      locationsLoading.value = true;
      try {
        const stores = (await getLocationsInfo(locationIds, [...REQUIRED_STORE_FIELDS])) as Array<StoreInfo>;
        if (Array.isArray(stores)) {
          stores.forEach((store: StoreInfo) => {
            setLocationInfo(store.locationNumber, store);
          });
        }
      } catch {
        locationIds.forEach((id) => {
          setLocationInfo(id, null);
        });
      } finally {
        locationsLoading.value = false;
      }
    }
  });

  watch(storeLocation, async (newStore, oldStore) => {
    const isNewStore = newStore && newStore !== oldStore && !eligibility.value[newStore];
    if (isNewStore) {
      inventoryLoading.value = true;
      try {
        const storeInventories = ((await getStoreInventoryInfo([newStore])) as StoreInventory[]) || [];
        setLocationEligibility(newStore, storeInventories[0] || null);
      } catch {
        setLocationEligibility(newStore, null);
      } finally {
        inventoryLoading.value = false;
        cslLoaded.value = true;
      }
    } else {
      cslLoaded.value = true;
    }
  });

  watch(sddZipCode, async (newZipCode, oldZipCode) => {
    if (newZipCode && newZipCode !== oldZipCode) {
      sddZipCodeLoading.value = true;
      try {
        const info = await (getLocationInfoByZipCode(newZipCode) as Promise<LocationInfo>);
        shipFromLocation.value = info?.locationNumber || null; // there is no location number if the store isn't eligible
      } catch {
        shipFromLocation.value = null;
      } finally {
        sddZipCodeLoading.value = false;
        sflLoaded.value = true;
      }
    } else if (!newZipCode) {
      shipFromLocation.value = null;
      sflLoaded.value = true;
    }
  });

  const unsInitLoaded = watch([sflLoaded, cslLoaded], ([_sflLoaded, _cslLoaded]) => {
    if (_sflLoaded && _cslLoaded) {
      unsInitLoaded();
      notifySubscribers();
    }
  });

  // Watch shipFromLocation changes on the client side (e.g. from zip code searches) and persist the SFL cookie
  watch(shipFromLocation, (newVal, oldVal) => {
    if (isClient && newVal !== oldVal) {
      Cookie.set('SFL', newVal?.toString() || '');
    }
  });

  const init = () => {
    setSddZipCode(getZipCode());
    const cslVal = parseInt(getCSLCookie(), 10) || null;
    setStoreLocation(cslVal);
    setBopsStore(getCommonStore());
  };

  const refreshBopsStore = () => {
    setBopsStore(getCommonStore());
  };

  const isLoading = computed(() => {
    return sddZipCodeLoading.value || locationsLoading.value || inventoryLoading.value;
  });

  const afterStoreInit = (fn: () => void) => {
    if (typeof fn === 'function') {
      observers.push(fn);
    }
  };

  return {
    sddZipCode,
    bopsStore,
    eligibility,
    locations,
    setSddZipCode,
    setBopsStore,
    setLocationEligibility,
    setLocationInfo,
    init,
    storeZipCode,
    storeLocation,
    sddLocationInfo,
    pickupLocationInfo,
    shipFromLocation,
    refreshBopsStore,
    setStoreLocation,
    sddZipCodeLoading,
    getItLabel,
    getItDeliveryType,
    sddOrderByMsg,
    pickUpOrderByMsg,
    locationsLoading,
    isLoading,
    bopsEligible,
    sddEligible,
    inventoryLoading,
    pickupRelativeDay,
    deliveryRelativeDay,
    afterStoreInit,
    isStoreClosed,
    isSddClosed,
    isStoreClosedToday,
    isSddClosedToday,
    isSddEligibleForBadge,
    isBopsEligibleForBadge,
  };
});

export default useDeliveryAndPickupStore;
